// @ts-nocheck
/**
 * rendererUtils.ts — Template V2 M3 Renderer Shared Utilities
 *
 * Helpers used by every renderer:
 *  - applyExpressionFallback — DEC-019 fail-open visibility/enabled
 *  - resolveValue            — ValueSource→concrete value (static or binding)
 *  - extractLayout           — LayoutBox→RenderedLayout
 *  - buildDescriptor         — assembles a RenderDescriptor
 *
 * No React Native, Tamagui, or app-level imports.
 */

import type { ValidationIssue } from '../../diagnostics';
import type { StyleBlock, ValueSource, LocalizedText } from '../../types/canonical';
import { resolveLocalizedText } from '../../../i18n/resolveLocalizedText';
import { normalizeTemplateFontId } from '../../types/templateFonts';
import { resolveRef } from '../binding';
import type { CompiledRuntimeNode } from '../compiler';
import { DEFAULT_STYLE_PADDING } from './styleDefaults';
import { computeScaledFontSize } from './scaleFontWithItemSize';
import type { ComponentRenderContext, RenderChrome, RenderDescriptor, RenderedLayout } from './types';

// ── DEC-019: expression engine fail-open ──────────────────────────────────────

/**
 * Coerces a resolved ValueSource result to boolean using strict `=== true`.
 * Missing / non-boolean values are false.
 */
function coerceStrictTrue(value: unknown): boolean {
  return value === true;
}

/**
 * Resolves a visibility/enabled ValueSource.
 * Expression kind fail-opens (true) when the engine is unavailable (DEC-019).
 * Binding/static use resolveValue + strict-true coercion (missing → false).
 */
function resolveVisibilityEnabledSource(
  context: ComponentRenderContext<unknown>,
  source: ValueSource<boolean>,
  slot: 'visibility' | 'enabled',
): boolean {
  if (source.kind === 'expression') {
    if (context.expressionEngine !== null && context.bindingContext !== undefined) {
      const result = context.expressionEngine.evaluate(
        source.expressionId,
        context.bindingContext,
        { profile: 'production', expectedType: 'boolean' },
      );
      context.issues.push(...result.issues);
      return result.ok && result.value === true;
    }
    context.issues.push({
      severity: 'warning',
      code: slot === 'visibility'
        ? 'render.visibility_expression_unavailable'
        : 'render.enabled_expression_unavailable',
      message:
        slot === 'visibility'
          ? `Expression-driven visibility unavailable for node "${context.node.id}"; ` +
            'rendering as visible (DEC-019 fail-open).'
          : `Expression-driven enabled state unavailable for node "${context.node.id}"; ` +
            'rendering as enabled (DEC-019 fail-open).',
      path: context.node.id,
    });
    return true;
  }

  return coerceStrictTrue(resolveValue(source, context));
}

/**
 * Resolves visibility and enabled state for a node.
 *
 * Prefer full ValueSource on the compiled node (static | binding | expression).
 * Expression kind fails open when the engine is unavailable (DEC-019).
 * Binding/static use strict `=== true` (missing binding → false).
 *
 * Falls back to legacy staticVisible/staticEnabled + expression ID fields when
 * no ValueSource was compiled onto the node.
 */
export function applyExpressionFallback(
  context: ComponentRenderContext<unknown>,
): { visible: boolean; enabled: boolean } {
  let visible = context.node.staticVisible ?? context.visible;
  let enabled = context.node.staticEnabled ?? context.enabled;

  if (context.node.visibilitySource !== undefined) {
    visible = resolveVisibilityEnabledSource(context, context.node.visibilitySource, 'visibility');
  } else if (context.node.visibilityExpressionId !== undefined) {
    if (context.expressionEngine !== null && context.bindingContext !== undefined) {
      const result = context.expressionEngine.evaluate(
        context.node.visibilityExpressionId,
        context.bindingContext,
        { profile: 'production', expectedType: 'boolean' },
      );
      context.issues.push(...result.issues);
      visible = result.ok && result.value === true;
    } else {
      context.issues.push({
        severity: 'warning',
        code: 'render.visibility_expression_unavailable',
        message:
          `Expression-driven visibility unavailable for node "${context.node.id}"; ` +
          'rendering as visible (DEC-019 fail-open).',
        path: context.node.id,
      });
      visible = true;
    }
  }

  if (context.node.enabledSource !== undefined) {
    enabled = resolveVisibilityEnabledSource(context, context.node.enabledSource, 'enabled');
  } else if (context.node.enabledExpressionId !== undefined) {
    if (context.expressionEngine !== null && context.bindingContext !== undefined) {
      const result = context.expressionEngine.evaluate(
        context.node.enabledExpressionId,
        context.bindingContext,
        { profile: 'production', expectedType: 'boolean' },
      );
      context.issues.push(...result.issues);
      enabled = result.ok && result.value === true;
    } else {
      context.issues.push({
        severity: 'warning',
        code: 'render.enabled_expression_unavailable',
        message:
          `Expression-driven enabled state unavailable for node "${context.node.id}"; ` +
          'rendering as enabled (DEC-019 fail-open).',
        path: context.node.id,
      });
      enabled = true;
    }
  }

  return { visible, enabled };
}

// ── Value resolution ──────────────────────────────────────────────────────────

function coerceFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

/**
 * Resolves a concrete value from a ValueSource<T>.
 *
 * - kind==='static'     → returns source.value
 * - kind==='binding'    → resolves source.ref via BindingResolver (DEC-036); display must not use binding.read alone
 * - kind==='expression' → expression engine when available
 */
export function resolveValue<T>(
  source: ValueSource<T>,
  context: ComponentRenderContext<unknown>,
): T | undefined {
  if (source.kind === 'static') return source.value;
  if (source.kind === 'binding') {
    if (context.bindingContext) {
      const result = resolveRef(source.ref, context.bindingContext);
      if (result.diagnostics) {
        context.issues.push(...result.diagnostics);
      }
      if (result.found) {
        return result.value as T;
      }
    }
    return undefined;
  }
  if (source.kind === 'expression') {
    if (!context.expressionEngine || !context.bindingContext) {
      return undefined;
    }
    const result = context.expressionEngine.evaluate(
      source.expressionId,
      context.bindingContext,
      { profile: 'production' },
    );
    context.issues.push(...result.issues);
    if (!result.ok || result.value === null) {
      return undefined;
    }
    return result.value as T;
  }
  return undefined;
}

export function looksLikeLocaleMap(value: unknown): value is Record<string, string> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  if ('kind' in (value as Record<string, unknown>)) return false;
  const entries = Object.values(value as Record<string, unknown>);
  if (entries.length === 0) return false;
  return entries.every((entry) => typeof entry === 'string');
}

/** Resolve a LocalizedText or plain string for on-canvas display. */
export function resolveDisplayString(
  value: unknown,
  locale: string | undefined,
  fallback = '',
): string {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') return value;
  if (looksLikeLocaleMap(value)) {
    return resolveLocalizedText(value as LocalizedText, locale ?? 'en', { fallback });
  }
  return fallback;
}

/** If a resolved ValueSource is a locale map, collapse it using the render locale. */
export function coerceResolvedDisplayValue(
  value: unknown,
  locale: string | undefined,
): unknown {
  if (looksLikeLocaleMap(value)) {
    return resolveLocalizedText(value as LocalizedText, locale ?? 'en');
  }
  return value;
}

// ── Style resolution ──────────────────────────────────────────────────────────

export function resolveStyleBlock(
  style: StyleBlock | undefined,
  context: ComponentRenderContext<unknown>,
): RenderChrome | undefined {
  if (!style) {
    return undefined;
  }

  const chrome: RenderChrome = {};
  const color = style.color ? resolveValue(style.color, context) : undefined;
  if (typeof color === 'string') {
    chrome.color = color;
  }
  const backgroundColor = style.backgroundColor ? resolveValue(style.backgroundColor, context) : undefined;
  if (typeof backgroundColor === 'string') {
    chrome.backgroundColor = backgroundColor;
  }
  const fontSize = style.fontSize ? coerceFiniteNumber(resolveValue(style.fontSize, context)) : undefined;
  if (fontSize !== undefined) {
    const scaleWithSize = style.scaleFontWithItemSize
      ? coerceStrictTrue(resolveValue(style.scaleFontWithItemSize, context))
      : false;
    if (scaleWithSize) {
      const item = context.bindingContext?.item;
      chrome.fontSize = computeScaledFontSize(
        fontSize,
        item?.width ?? 0,
        item?.height ?? 0,
        item?.orgWidth,
        item?.orgHeight,
      );
    } else {
      chrome.fontSize = fontSize;
    }
  }
  const borderColor = style.borderColor ? resolveValue(style.borderColor, context) : undefined;
  if (typeof borderColor === 'string') {
    chrome.borderColor = borderColor;
  }
  const borderWidth = style.borderWidth != null
    ? coerceFiniteNumber(resolveValue(style.borderWidth, context))
    : undefined;
  if (borderWidth !== undefined) {
    chrome.borderWidth = borderWidth;
  }
  const borderRadius = style.borderRadius ? coerceFiniteNumber(resolveValue(style.borderRadius, context)) : undefined;
  if (borderRadius !== undefined) {
    chrome.borderRadius = borderRadius;
  }
  const opacity = style.opacity ? coerceFiniteNumber(resolveValue(style.opacity, context)) : undefined;
  if (opacity !== undefined) {
    chrome.opacity = opacity;
  }
  // fontWeight — plain union string, no ValueOrExpression wrapper
  if (style.fontWeight !== undefined) {
    chrome.fontWeight = style.fontWeight;
  }
  // fontFamily — closed catalog id (DEC-064); omitted/unknown → nunitoSans
  chrome.fontFamily = normalizeTemplateFontId(style.fontFamily);
  // padding — number or per-side object; missing → DEFAULT_STYLE_PADDING
  chrome.padding = style.padding !== undefined ? style.padding : DEFAULT_STYLE_PADDING;
  // textAlign — all four allowed values
  if (
    style.textAlign === 'left' ||
    style.textAlign === 'center' ||
    style.textAlign === 'right' ||
    style.textAlign === 'justify'
  ) {
    chrome.textAlign = style.textAlign;
  }
  // fontStyle — maps V1 isItalic
  if (style.fontStyle === 'normal' || style.fontStyle === 'italic') {
    chrome.fontStyle = style.fontStyle;
  }
  // textDecoration — maps V1 isUnderlined / isStrikethrough
  if (
    style.textDecoration === 'none' ||
    style.textDecoration === 'underline' ||
    style.textDecoration === 'line-through' ||
    style.textDecoration === 'underline line-through'
  ) {
    chrome.textDecoration = style.textDecoration;
  }
  // verticalAlign — maps V1 verticalAlign
  if (
    style.verticalAlign === 'top' ||
    style.verticalAlign === 'center' ||
    style.verticalAlign === 'bottom'
  ) {
    chrome.verticalAlign = style.verticalAlign;
  }
  // elevation — plain enum 0 | 1 | 2 (including explicit 0)
  if (style.elevation === 0 || style.elevation === 1 || style.elevation === 2) {
    chrome.elevation = style.elevation;
  }
  // bevel — plain enum raised | inset
  if (style.bevel === 'raised' || style.bevel === 'inset') {
    chrome.bevel = style.bevel;
  }

  return Object.keys(chrome).length > 0 ? chrome : undefined;
}

// ── Layout extraction ─────────────────────────────────────────────────────────

/** Extracts the geometry fields from CompiledRuntimeNode.layout. */
export function extractLayout(
  node: CompiledRuntimeNode,
  context?: ComponentRenderContext<unknown>,
): RenderedLayout {
  let rotation: number | undefined;
  const rawRotation = node.layout.rotation;
  if (typeof rawRotation === 'number') {
    rotation = rawRotation;
  } else if (rawRotation != null && context != null) {
    const resolved = resolveValue(rawRotation, context);
    if (typeof resolved === 'number' && Number.isFinite(resolved)) {
      rotation = resolved;
    }
  }
  return {
    x: node.layout.x,
    y: node.layout.y,
    width: node.layout.width,
    height: node.layout.height,
    zIndex: node.layout.zIndex,
    rotation,
  };
}

// ── Descriptor assembly ───────────────────────────────────────────────────────

/**
 * Assembles a RenderDescriptor from its parts.
 * Omits 'children' and 'diagnostics' fields when empty/undefined so the
 * serialized output stays compact.
 */
export function buildDescriptor(
  node: CompiledRuntimeNode,
  visible: boolean,
  enabled: boolean,
  props: Record<string, unknown>,
  options?: {
    children?: RenderDescriptor[];
    diagnostics?: ValidationIssue[];
    chrome?: RenderChrome;
    renderContext?: ComponentRenderContext<unknown>;
  },
): RenderDescriptor {
  const descriptor: RenderDescriptor = {
    nodeId: node.id,
    type: node.type,
    layout: extractLayout(node, options?.renderContext),
    visible,
    enabled,
    props,
  };
  if (options?.chrome && Object.keys(options.chrome).length > 0) {
    descriptor.chrome = options.chrome;
  }
  if (options?.children && options.children.length > 0) {
    descriptor.children = options.children;
  }
  if (options?.diagnostics && options.diagnostics.length > 0) {
    descriptor.diagnostics = options.diagnostics;
  }
  return descriptor;
}
